import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { X, Eye, Edit3, UserPlus } from "lucide-react";

export function LinkEventDialog({ open, onOpenChange, eventSearch, setEventSearch, filteredEvents, onLinkEvent }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="uppercase tracking-wider text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Link to Event</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">Search and select an event to link this note to.</DialogDescription>
        </DialogHeader>
        <Input data-testid="event-link-search" placeholder="Search events..." value={eventSearch} onChange={e => setEventSearch(e.target.value)} className="bg-secondary/30 border-border mb-3" autoFocus />
        <div className="max-h-64 overflow-y-auto space-y-1">
          {filteredEvents.map(ev => (
            <button key={ev.event_id} data-testid={`link-event-option-${ev.event_id}`} className="w-full text-left px-3 py-2 rounded-sm hover:bg-secondary/40 transition-colors" onClick={() => onLinkEvent(ev)}>
              <div className="text-sm font-medium">{ev.name}</div>
              <div className="text-xs text-muted-foreground">{ev.start_date} &middot; {ev.status}</div>
            </button>
          ))}
          {filteredEvents.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No events found</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ShareNoteDialog({ open, onOpenChange, activeNote, shareableUsers, sharePermission, setSharePermission, onShareWithUser, onRemoveShare }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="uppercase tracking-wider text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Share Note</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">Share this note with team members.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {activeNote?.shared_with?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Shared with</p>
              {activeNote.shared_with.map(share => (
                <div key={share.user_id} className="flex items-center justify-between py-1.5 px-2 bg-secondary/20 rounded-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{share.user_name}</span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                      {share.permission === "edit" ? <><Edit3 className="w-2.5 h-2.5 mr-0.5" />Edit</> : <><Eye className="w-2.5 h-2.5 mr-0.5" />View</>}
                    </Badge>
                  </div>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400" onClick={() => onRemoveShare(share.user_id)} data-testid={`remove-share-${share.user_id}`}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {shareableUsers.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Add people</p>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-muted-foreground">Permission:</span>
                <Select value={sharePermission} onValueChange={setSharePermission}>
                  <SelectTrigger className="w-28 h-7 text-xs bg-secondary/30 border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view">View only</SelectItem>
                    <SelectItem value="edit">Can edit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {shareableUsers.map(u => (
                  <button key={u.user_id} className="w-full text-left px-3 py-2 rounded-sm hover:bg-secondary/40 transition-colors flex items-center justify-between" onClick={() => onShareWithUser(u.user_id)} data-testid={`share-user-${u.user_id}`}>
                    <div>
                      <div className="text-sm">{u.name}</div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                    </div>
                    <UserPlus className="w-4 h-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {shareableUsers.length === 0 && (!activeNote?.shared_with?.length) && (
            <p className="text-xs text-muted-foreground text-center py-4">No team members to share with. Add team members first.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteNoteDialog({ open, onClose, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-sm bg-card border-border">
        <DialogHeader>
          <DialogTitle>Delete Note</DialogTitle>
          <DialogDescription>This action cannot be undone.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" size="sm" data-testid="confirm-delete-note" onClick={onConfirm}>Delete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
